/**
 * Maps app routes to Permissions Manager page keys.
 * Page keys must match backend page_permissions.PAGE_KEY values.
 */

import type { EffectiveRole } from '@/shared/utils/roles';

export const PAGE_KEYS = [
  'dashboard',
  'tasks',
  'projects',
  'team',
  'departments',
  'reviews',
  'users',
  'settings',
  'calendar',
  'delegated',
  'documents',
  'tickets',
] as const;

export type PageKey = (typeof PAGE_KEYS)[number];

/** Route path prefix → page permission key */
export const PATH_TO_PAGE_KEY: Record<string, PageKey> = {
  '/dashboard': 'dashboard',
  '/tasks': 'tasks',
  '/delegated': 'delegated',
  '/projects': 'projects',
  '/calendar': 'calendar',
  '/documents': 'documents',
  '/tickets': 'tickets',
  '/team': 'team',
  '/departments': 'departments',
  '/reviews': 'reviews',
  '/users': 'users',
  '/settings': 'settings',
};

export const PAGE_KEY_TO_PATH: Record<PageKey, string> = {
  dashboard: '/dashboard',
  tasks: '/tasks',
  projects: '/projects',
  team: '/team',
  departments: '/departments',
  reviews: '/reviews',
  users: '/users',
  settings: '/settings',
  calendar: '/calendar',
  delegated: '/delegated',
  documents: '/documents',
  tickets: '/tickets',
};

/**
 * Dynamic page permissions are enforced for engineers first.
 * Admin / super_admin keep full access for now.
 */
export function shouldEnforcePagePermissions(role: EffectiveRole): boolean {
  return role === 'engineer';
}

export function getPageKeyFromPath(pathname: string): PageKey | null {
  if (pathname === '/' || pathname === '') return 'dashboard';
  const match = Object.keys(PATH_TO_PAGE_KEY)
    .sort((a, b) => b.length - a.length)
    .find((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return match ? PATH_TO_PAGE_KEY[match] : null;
}

/** Count how many pages a role currently has enabled (from a page→role map). */
export function countEnabledPagesForRole(
  pageAccessMap: Map<string, Map<string, boolean>>,
  role: string
): number {
  let count = 0;
  pageAccessMap.forEach((roleMap) => {
    if (roleMap.get(role)) count += 1;
  });
  return count;
}
